import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ChevronLeft, Factory } from "lucide-react";
import { StoreLayout } from "@/components/StoreLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { api, formatINR, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function Cart() {
  const navigate = useNavigate();
  const { shopLoggedIn } = useAuth();
  const { cart, setCart, loadCart, updateQty, removeItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/cart"); setCart(data); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (!shopLoggedIn) { navigate("/login"); return; }
    load();
  }, []);

  const handleQty = async (it, newQty) => {
    await updateQty(it.product_id, newQty, it.product);
  };
  const handleRemove = async (pid) => {
    const ok = await removeItem(pid);
    if (ok) toast("Removed from cart");
  };
  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders");
      toast.success(`Order ${data.order_no} placed — awaiting approval from our team`);
      loadCart();
      navigate("/orders");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not place order"); }
    finally { setPlacing(false); }
  };

  return (
    <StoreLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate("/products")} className="mb-4 gap-1 -ml-2"><ChevronLeft className="h-4 w-4" /> Continue shopping</Button>
        <h1 className="font-display text-3xl mb-6">Your Cart</h1>
        {loading ? <div className="py-24 text-center text-muted-foreground">Loading...</div>
          : cart.items.length === 0 ? (
          <div className="py-24 text-center">
            <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">Your cart is empty.</p>
            <Button className="mt-4" onClick={() => navigate("/products")}>Browse jewellery</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
            <div className="space-y-4">
              {cart.items.map((it) => {
                const stock = Number(it.product?.stock ?? 0);
                const inStock = stock > 0;
                const atLimit = inStock && it.quantity >= stock;
                return (
                  <Card key={it.product_id} data-testid="cart-line-item" className="p-4 flex gap-4">
                    <img src={resolveImg((it.product.images || [])[0])} alt={it.product.name} className="h-24 w-24 rounded-lg object-cover cursor-pointer" onClick={() => navigate(`/product/${it.product_id}`)} />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-display text-lg">{it.product.name}</h3>
                            {!inStock && (
                              <Badge data-testid="cart-make-to-order-badge" className="bg-[hsl(var(--gold-light))] text-[hsl(var(--gold-dark))] border border-[hsl(var(--gold-dark))]/30 gap-1 text-[10px]">
                                <Factory className="h-3 w-3" /> Made to Order
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{it.product.sku}</p>
                        </div>
                        <button data-testid="cart-remove-button" onClick={() => handleRemove(it.product_id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center border border-border rounded-lg">
                            <button data-testid="cart-qty-decrease" className="px-2 py-1" onClick={() => handleQty(it, it.quantity - 1)}><Minus className="h-3.5 w-3.5" /></button>
                            <span className="px-3 font-mono text-sm">{it.quantity}</span>
                            <button data-testid="cart-qty-increase" disabled={atLimit} title={atLimit ? "No more available" : ""} className="px-2 py-1 disabled:opacity-40 disabled:pointer-events-none" onClick={() => handleQty(it, it.quantity + 1)}><Plus className="h-3.5 w-3.5" /></button>
                          </div>
                          {inStock && atLimit && <span className="text-[11px] text-muted-foreground">Max available reached</span>}
                        </div>
                        <span className="font-mono text-[hsl(var(--primary))]">{formatINR(it.line_total)}</span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <div>
              <Card className="p-6 sticky top-24">
                <h3 className="font-display text-xl mb-4">Order Summary</h3>
                <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Items ({cart.count})</span></div>
                <Separator className="my-3" />
                <div className="flex justify-between mb-5"><span className="font-display text-lg">Total</span><span className="font-mono text-lg text-[hsl(var(--primary))]">{formatINR(cart.subtotal)}</span></div>
                <Button data-testid="cart-checkout-button" className="w-full h-12" onClick={placeOrder} disabled={placing}>{placing ? "Placing order..." : "Place Order"}</Button>
                <p className="text-xs text-muted-foreground text-center mt-3">Order will be reviewed and approved by our team · GST included in item prices</p>
              </Card>
            </div>
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
